/**
 * ChainVault WP - Price Feed Utility
 *
 * Fetches real cryptocurrency prices from CoinGecko API.
 * Used to display USD values in the wallet interface.
 *
 * @package ChainVault
 * @since   1.0.0
 */

/**
 * CoinGecko API base URL
 * Free tier allows 10-30 calls per minute
 */
const COINGECKO_BASE = 'https://api.coingecko.com/api/v3';

/**
 * Mapping of our network keys to CoinGecko coin IDs
 */
const COIN_IDS = {
    ethereum: 'ethereum',
    bsc:      'binancecoin',
    polygon:  'matic-network',
    base:     'ethereum',
    solana:   'solana',
    bitcoin:  'bitcoin',
};

/**
 * Cache for prices to avoid hitting rate limits
 * Prices are cached for 60 seconds
 */
let priceCache = {};
let lastFetch = 0;
const CACHE_DURATION = 60 * 1000;

/**
 * Fetch current prices for all supported coins
 * Returns an object with coin IDs as keys and USD prices as values
 *
 * @return {Promise<object>} Price data object
 */
export async function fetchAllPrices() {
    const now = Date.now();

    // Return cached prices if still fresh
    if ( now - lastFetch < CACHE_DURATION && Object.keys( priceCache ).length > 0 ) {
        return priceCache;
    }

    try {
        const ids = Object.values( COIN_IDS ).join( ',' );
        const url = `${ COINGECKO_BASE }/simple/price?ids=${ ids }&vs_currencies=usd&include_24hr_change=true`;

        const response = await fetch( url );
        const data = await response.json();

        priceCache = data;
        lastFetch = now;

        return data;
    } catch ( error ) {
        console.error( 'Failed to fetch prices:', error );
        return priceCache;
    }
}

/**
 * Get USD price for a specific network
 *
 * @param {string} network  Network key (ethereum, bsc, polygon, solana)
 * @param {object} prices   Price data from fetchAllPrices()
 * @return {number|null}    USD price or null if not found
 */
export function getNetworkPrice( network, prices ) {
    const coinId = COIN_IDS[ network ];
    if ( ! coinId || ! prices[ coinId ] ) return null;
    return prices[ coinId ].usd;
}

/**
 * Get 24 hour price change for a specific network
 *
 * @param {string} network  Network key
 * @param {object} prices   Price data from fetchAllPrices()
 * @return {number|null}    24hr change percentage or null
 */
export function getNetworkPriceChange( network, prices ) {
    const coinId = COIN_IDS[ network ];
    if ( ! coinId || ! prices[ coinId ] ) return null;
    return prices[ coinId ].usd_24h_change;
}

/**
 * Convert a crypto amount to USD
 *
 * @param {number} amount   Amount in crypto
 * @param {string} network  Network key
 * @param {object} prices   Price data from fetchAllPrices()
 * @return {string}         Formatted USD value like "$16.42"
 */
export function toUSD( amount, network, prices ) {
    const price = getNetworkPrice( network, prices );
    if ( ! price ) return '$0.00';
    const usd = parseFloat( amount ) * price;
    return `$${ usd.toFixed( 2 ) }`;
}

/**
 * Format a USD amount for display
 *
 * @param {number} amount USD amount
 * @return {string} Formatted string like "$1,234.56"
 */
export function formatUSD( amount ) {
    return new Intl.NumberFormat( 'en-US', {
        style:                 'currency',
        currency:              'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    } ).format( amount );
}